import { FC, useEffect, useRef, useState } from 'react';

import type { SanityReadinessLoadingStep } from '@lib/sanity/types';

type SequentialLoadingBarsProps = {
  steps: SanityReadinessLoadingStep[];
  onComplete: () => void;
};

/**
 * Sequential progress bars: each fills over its configured duration,
 * turns green and gets a checkmark, then the next starts. `onComplete`
 * fires once after the final bar.
 */
const SequentialLoadingBars: FC<SequentialLoadingBarsProps> = ({
  steps,
  onComplete
}) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [fillingStepIndex, setFillingStepIndex] = useState(-1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const hasCompletedRef = useRef(false);

  // Kick off the fill animation one frame after the step becomes active,
  // so the browser paints the bar at 0% first and the CSS transition fires.
  useEffect(() => {
    if (activeStepIndex >= steps.length) return;

    const rafId = requestAnimationFrame(() => {
      setFillingStepIndex(activeStepIndex);
    });

    return () => cancelAnimationFrame(rafId);
  }, [activeStepIndex, steps.length]);

  useEffect(() => {
    if (activeStepIndex >= steps.length) {
      if (!hasCompletedRef.current) {
        hasCompletedRef.current = true;
        onComplete();
      }
      return;
    }

    if (fillingStepIndex !== activeStepIndex) return;

    const currentStep = steps[activeStepIndex];
    const timer = setTimeout(() => {
      setCompletedSteps((previous) => new Set([...previous, activeStepIndex]));
      setActiveStepIndex((previous) => previous + 1);
    }, currentStep.durationMs);

    return () => clearTimeout(timer);
  }, [activeStepIndex, fillingStepIndex, steps, onComplete]);

  return (
    <div className="space-y-5">
      {steps.map((step, index) => {
        const isCompleted = completedSteps.has(index);
        const isActive = index === activeStepIndex;
        const isFilling = index === fillingStepIndex;
        const isPending = index > activeStepIndex;

        return (
          <div key={step._key}>
            <div className="mb-1.5 flex items-center justify-between">
              <span
                className={`font-body text-sm font-medium ${
                  isPending ? 'text-neutral-400' : 'text-neutral-700'
                }`}
              >
                {step.label}
              </span>
              {isCompleted && (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1BC669"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-200">
              <div
                className={`h-full rounded-full transition-all ease-linear ${
                  isCompleted
                    ? 'bg-greenfriday'
                    : isActive
                    ? 'bg-primary-600'
                    : 'bg-neutral-200'
                }`}
                style={{
                  width: isCompleted
                    ? '100%'
                    : isActive && isFilling
                    ? '100%'
                    : '0%',
                  transitionDuration:
                    isActive && isFilling ? `${step.durationMs}ms` : '300ms'
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SequentialLoadingBars;
